import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import {
  canAccessClubModule,
  findClubModuleByPath,
  isClubModuleEnabled,
} from '@/config/club-modules';
import { isClubNotProvisioned, useMyClub } from '@/hooks/use-club';
import type { ClubCapability } from '@/types/club-membership';

/**
 * Garde des pages de gestion du club.
 *
 * Retirer une entrée du menu ne suffit pas : l'URL reste tapable, et un lien
 * partagé continuerait d'ouvrir un module que le club a éteint. La garde couvre
 * les deux cas — module désactivé, et compte sans fiche club.
 *
 * Les données restent protégées côté serveur ; ce contrôle sert la lisibilité.
 */
export default function ClubModuleRoute({ capability }: { capability?: ClubCapability } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: club, isLoading, error } = useMyClub();

  const clubModule = findClubModuleByPath(location.pathname);

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-emerald-200 border-t-emerald-600" />
      </div>
    );
  }

  // Sans fiche club, aucune de ces pages n'a de contenu : le tableau de bord
  // porte déjà l'explication, on y renvoie plutôt que de la dupliquer.
  if (isClubNotProvisioned(error)) {
    return <Navigate to="/club-dashboard" replace />;
  }

  // Le rôle ne donne pas accès à cet écran : on renvoie le membre chez lui
  // plutôt que d'afficher une page dont chaque appel serait refusé.
  const capabilities = club?.capabilities;
  const lacksExplicitCapability =
    capability && capabilities && capabilities.length > 0 && !capabilities.includes(capability);

  if (lacksExplicitCapability) {
    return <Navigate to="/mon-club" replace />;
  }

  if (clubModule && !canAccessClubModule(capabilities, clubModule.key)) {
    return <Navigate to="/mon-club" replace />;
  }

  if (clubModule && !isClubModuleEnabled(club?.enabled_modules, clubModule.key)) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 text-gray-500">
            <Lock className="h-7 w-7" />
          </div>
          <h1 className="text-lg font-bold text-gray-900">Module désactivé</h1>
          <p className="mt-2 text-sm text-gray-600">
            Le module <strong>{clubModule.label}</strong> est désactivé pour ce club. Réactivez-le
            depuis l'administration du club pour y accéder de nouveau.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <button
              onClick={() => navigate('/club/administration')}
              className="rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
            >
              Gérer les modules
            </button>
            <button
              onClick={() => navigate('/club-dashboard')}
              className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
            >
              Retour au tableau de bord
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <Outlet />;
}
